"use client";

import { useState, useRef, useEffect } from "react";
import Image from "next/image";
import SmoothImage from "@/utils/smoothImage";
import VideoModal from "./VideoModal";
import { ChevronLeft, ChevronRight } from "lucide-react";
import SectionTitle from "@/components/SectionTitle";
import {
  motion,
  useMotionValue,
  useMotionValueEvent,
  animate,
  PanInfo,
} from "framer-motion";
import { useResponsive } from "@/hooks/useResponsive";
import { cn } from "@/utils/cn";

interface SpecItem {
  label: string;
  value: string;
}

interface ApartmentLayoutItem {
  title: string;
  image: string;
  desc: string;
  specs: SpecItem[];
  beds: number | string;
  baths: number;
  area: string;
  type: string;
  price: string;
  badgeGreen: string;
  badgeDark: string;
}

interface ProjectOverviewProps {
  videoUrl: string;
  videoCoverImage: string;
  galleryImages: string[];
  onImageClick: (index: number) => void;
  subtitle?: string;
  title?: string;
  description?: string;
  tableHeader?: string;
  specs?: SpecItem[];
}

export default function ProjectOverview({
  videoUrl,
  videoCoverImage,
  galleryImages,
  onImageClick,
  subtitle,
  title,
  description,
  tableHeader,
  specs,
}: ProjectOverviewProps) {
  const [videoOpen, setVideoOpen] = useState(false);

  return (
    <>
      {/* SECTION 02: GIỚI THIỆU */}
      <section className="py-12 md:py-16">
        <div className="max-w-7xl mx-auto px-6 md:px-8">
          {/* Header Section */}
          <div className="text-center max-w-3xl mx-auto mb-16 space-y-4">
            <SectionTitle
              title={title}
              subtitle={subtitle}
              theme="green"
              subtitleColor="primaryHover"
              underlineColor="primaryHover"
            />
            <p className="text-sm text-black/80 leading-relaxed font-medium pt-2 text-justify md:text-center">
              {description}
            </p>
          </div>

          {/* Main Grid: Info list & Video */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
            {/* Left Column: Specifications table */}
            <div className="lg:col-span-6">
              <h4 className="text-xl md:text-2xl font-bold text-primaryHover uppercase tracking-wide">
                {tableHeader}
              </h4>
              <div className="w-24 h-1 bg-primaryHover mt-3" />
              <div className="w-full h-px bg-gray-300 mt-2 mb-6" />

              <div className="w-full space-y-0">
                {specs?.map((spec, index) => {
                  const isLast = specs ? index === specs.length - 1 : false;
                  return (
                    <div
                      key={index}
                      className={`flex flex-col md:flex-row py-2 border-b ${
                        isLast
                          ? "border-gray-400"
                          : "border-dashed border-gray-400"
                      }`}
                    >
                      <div className="w-full md:w-1/3 text-primaryHover font-bold mb-1.5 md:mb-0 text-sm">
                        {spec.label}:
                      </div>
                      <div className="w-full md:w-2/3 text-gray-800  leading-relaxed text-sm">
                        {spec.value}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Right Column: Staggered Image Grid Collage */}
            <div className="lg:col-span-6 w-full">
              <div className="grid grid-cols-2 gap-3 md:gap-4 items-start">
                {/* Column 1 */}
                <div className="flex flex-col gap-3 md:gap-4">
                  {/* Top Left: Video Playbox (Tall Portrait) */}
                  <div
                    onClick={() => setVideoOpen(true)}
                    className="relative w-full aspect-[3/4] rounded-2xl overflow-hidden shadow-lg border border-black/5 group cursor-pointer"
                  >
                    {videoCoverImage ? (
                      <SmoothImage
                        src={videoCoverImage}
                        alt="Project Intro Video Cover"
                        fill
                        sizes="(min-width: 1024px) 50vw, 100vw"
                        className="object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                    ) : (
                      <div className="w-full h-full bg-thirdHover" />
                    )}
                    <div className="absolute inset-0 bg-black/15 group-hover:bg-black/10 transition duration-300" />

                    {/* YouTube Red Play Button overlay */}
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="relative w-16 h-11 bg-red-600 rounded-xl flex items-center justify-center shadow-lg transition-transform duration-300 group-hover:scale-110">
                        <div className="w-0 h-0 border-t-[8px] border-t-transparent border-b-[8px] border-b-transparent border-l-[14px] border-l-white ml-1" />
                      </div>
                    </div>
                  </div>

                  {/* Bottom Left: Gallery Image (Square-ish) */}
                  <div
                    onClick={() => onImageClick?.(0)}
                    className="relative w-full aspect-[4/3] rounded-2xl overflow-hidden shadow-lg border border-black/5 group cursor-pointer"
                  >
                    {galleryImages?.[0] ? (
                      <SmoothImage
                        src={galleryImages[0]}
                        alt="Project Image 1"
                        fill
                        sizes="(min-width: 1024px) 50vw, 100vw"
                        className="object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                    ) : (
                      <div className="w-full h-full bg-gray-200" />
                    )}
                    <div className="absolute inset-0 bg-black/5 group-hover:bg-black/0 transition duration-300" />
                  </div>
                </div>

                {/* Column 2 */}
                <div className="flex flex-col gap-3 md:gap-4">
                  {/* Top Right: Gallery Image (Square-ish) */}
                  <div
                    onClick={() => onImageClick?.(1)}
                    className="relative w-full aspect-[4/3] rounded-2xl overflow-hidden shadow-lg border border-black/5 group cursor-pointer"
                  >
                    {galleryImages?.[1] ? (
                      <SmoothImage
                        src={galleryImages[1]}
                        alt="Project Image 2"
                        fill
                        sizes="(min-width: 1024px) 50vw, 100vw"
                        className="object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                    ) : (
                      <div className="w-full h-full bg-gray-200" />
                    )}
                    <div className="absolute inset-0 bg-black/5 group-hover:bg-black/0 transition duration-300" />
                  </div>

                  {/* Bottom Right: Gallery Image (Tall Portrait) */}
                  <div
                    onClick={() => onImageClick?.(2)}
                    className="relative w-full aspect-[3/4] rounded-2xl overflow-hidden shadow-lg border border-black/5 group cursor-pointer"
                  >
                    {galleryImages?.[2] ? (
                      <>
                        <SmoothImage
                          src={galleryImages[2]}
                          alt="Project Image 3"
                          fill
                          sizes="(min-width: 1024px) 50vw, 100vw"
                          className="object-cover transition-transform duration-500 group-hover:scale-105"
                        />
                        {galleryImages.length > 3 && (
                          <div className="absolute inset-0 bg-black/60 flex flex-col items-center justify-center text-white transition duration-300 group-hover:bg-black/50">
                            <span className="text-xl font-black">
                              +{galleryImages.length - 3}
                            </span>
                            <span className="text-[9px] uppercase font-bold tracking-wider opacity-90 mt-0.5">
                              hình ảnh
                            </span>
                          </div>
                        )}
                      </>
                    ) : (
                      <div className="w-full h-full bg-gray-200" />
                    )}
                    <div className="absolute inset-0 bg-black/5 group-hover:bg-black/0 transition duration-300" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Video Modal Player */}
      <VideoModal
        open={videoOpen}
        onClose={() => setVideoOpen(false)}
        videoUrl={videoUrl}
      />
    </>
  );
}

interface ApartmentTypesProps {
  layoutsSubtitle?: string;
  layoutsTitle?: string;
  apartmentLayouts?: ApartmentLayoutItem[];
  onProductClick: (product: any) => void;
  onConsultClick?: (productName: string) => void;
}

import { useDomSlider } from "@/hooks/useAutoSlider";

export function ApartmentTypes({
  layoutsSubtitle = "Các loại diện tích căn hộ",
  layoutsTitle = "THIẾT KẾ CHI TIẾT CĂN HỘ",
  apartmentLayouts,
  onProductClick,
  onConsultClick,
}: ApartmentTypesProps) {
  const { isMobile } = useResponsive();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const x = useMotionValue(0);

  const [containerWidth, setContainerWidth] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);

  const REAL_COUNT = apartmentLayouts?.length || 0;
  const itemsPerPage = containerWidth < 640 ? 1 : containerWidth < 1024 ? 2 : 4;
  const GAP = 24;
  const itemWidth =
    containerWidth > 0
      ? (containerWidth - (itemsPerPage - 1) * GAP) / itemsPerPage
      : 280;
  const COL_WIDTH = itemWidth + GAP;

  const maxCol = Math.max(0, REAL_COUNT - itemsPerPage);
  const maxScrollX = Math.max(0, maxCol * COL_WIDTH);
  const totalDots = REAL_COUNT > itemsPerPage ? maxCol + 1 : 0;

  useMotionValueEvent(x, "change", (latest) => {
    let col = Math.round(Math.abs(latest) / COL_WIDTH);
    col = Math.max(0, Math.min(col, maxCol));
    setActiveIndex(col);
  });

  useEffect(() => {
    if (!containerRef.current) return;

    const observer = new ResizeObserver((entries) => {
      const width = entries[0].contentRect.width;
      setContainerWidth(width);
    });

    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  const handleDragEnd = (
    _event: MouseEvent | TouchEvent | PointerEvent,
    info: PanInfo,
  ) => {
    setTimeout(() => {
      setIsDragging(false);
    }, 50);

    if (isMobile) return;

    const rawX = x.get();
    const clamped = Math.max(-maxScrollX, Math.min(0, rawX));
    let col = Math.round(Math.abs(clamped) / COL_WIDTH);

    if (
      Math.abs(info.offset.x) > COL_WIDTH * 0.25 ||
      Math.abs(info.velocity.x) > 600
    ) {
      col += info.offset.x < 0 ? 1 : -1;
    }

    col = Math.max(0, Math.min(col, maxCol));
    setActiveIndex(col);
    const target = Math.max(-maxScrollX, Math.min(0, -col * COL_WIDTH));

    animate(x, target, {
      type: "spring",
      stiffness: 320,
      damping: 30,
    });
  };

  const handleGoTo = (idx: number) => {
    if (isDragging) return;
    const clampedIdx = Math.max(0, Math.min(idx, maxCol));
    setActiveIndex(clampedIdx);
    const target = Math.max(-maxScrollX, Math.min(0, -clampedIdx * COL_WIDTH));
    animate(x, target, {
      type: "spring",
      stiffness: 320,
      damping: 30,
    });
  };

  const handlePrev = () => {
    if (isDragging) return;
    const rawX = x.get();
    let col = Math.round(Math.abs(rawX) / COL_WIDTH) - 1;
    col = Math.max(0, col);
    handleGoTo(col);
  };

  const handleNext = () => {
    if (isDragging) return;
    const rawX = x.get();
    let col = Math.round(Math.abs(rawX) / COL_WIDTH) + 1;
    col = Math.min(maxCol, col);
    handleGoTo(col);
  };

  return (
    <section className="py-12 md:py-16 bg-transparent">
      <div className="space-y-6 w-full px-6 md:px-8 max-w-7xl mx-auto">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <SectionTitle
            title={layoutsTitle}
            subtitle={layoutsSubtitle}
            theme="green"
            subtitleColor="primaryHover"
            underlineColor="primaryHover"
            align="left"
            showLine={false}
          />
          {/* Navigation Arrows */}
          {totalDots > 1 && (
            <div className="flex items-center gap-2.5 shrink-0 self-end sm:self-auto">
              <button
                onClick={handlePrev}
                className="w-10 h-10 rounded-full border border-black/10 flex items-center justify-center text-primaryHover hover:bg-primaryHover hover:text-white transition-all duration-300 active:scale-95 shadow-sm hover:shadow-md cursor-pointer"
                aria-label="Previous slide"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <button
                onClick={handleNext}
                className="w-10 h-10 rounded-full border border-black/10 flex items-center justify-center text-primaryHover hover:bg-primaryHover hover:text-white transition-all duration-300 active:scale-95 shadow-sm hover:shadow-md cursor-pointer"
                aria-label="Next slide"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          )}
        </div>

        <div className="relative w-full overflow-hidden -mx-2 px-2 -my-3 py-3">
          <div
            ref={containerRef}
            className="w-full overflow-x-hidden overflow-y-visible py-2"
          >
            <motion.div
              className="slider flex gap-6"
              drag="x"
              dragDirectionLock
              dragMomentum
              dragElastic={0.08}
              dragTransition={{ power: 0.2, timeConstant: 200 }}
              dragConstraints={{ left: -maxScrollX, right: 0 }}
              style={{ x }}
              onDragStart={() => setIsDragging(true)}
              onDragEnd={handleDragEnd}
            >
              {apartmentLayouts?.map((layout, idx) => (
                <div
                  key={idx}
                  style={{ width: `${itemWidth}px` }}
                  onClick={(e) => {
                    if (isDragging) {
                      e.preventDefault();
                      return;
                    }
                    onProductClick?.(layout);
                  }}
                  className="shrink-0 group bg-white rounded-2xl overflow-hidden border border-black/5 hover:border-primaryHover/20 shadow-md hover:shadow-xl transition-all duration-500 ease-out hover:-translate-y-1.5 flex flex-col cursor-pointer select-none"
                >
                  {/* Image Area */}
                  <div className="relative aspect-[4/3] w-full bg-black/5 overflow-hidden">
                    <SmoothImage
                      src={layout.image}
                      alt={layout.title}
                      fill
                      sizes="(min-width: 1024px) 400px, 100vw"
                      className="object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                      draggable={false}
                    />
                    {/* Badges */}
                    {layout.badgeGreen && (
                      <div className="absolute top-3 left-3 bg-success text-white text-[9px] font-extrabold uppercase tracking-wide px-2 py-0.5 rounded shadow-sm">
                        {layout.badgeGreen}
                      </div>
                    )}
                    {layout.badgeDark && (
                      <div className="absolute top-3 right-3 bg-black/75 text-white text-[9px] font-extrabold uppercase tracking-wide px-2 py-0.5 rounded shadow-sm">
                        {layout.badgeDark}
                      </div>
                    )}
                    {/* Price overlay at bottom left */}
                    {layout.price && (
                      <div className="absolute bottom-3 left-3 bg-black/60 backdrop-blur-xs text-white text-xs font-extrabold px-2.5 py-1 rounded">
                        {layout.price}
                      </div>
                    )}
                  </div>

                  {/* Content Area */}
                  <div className="p-4 flex flex-col flex-1">
                    <h5 className="font-extrabold text-sm text-primary group-hover:text-primaryHover transition-colors mb-1.5">
                      {layout.title}
                    </h5>
                    <p className="text-[11px] text-black/55 leading-relaxed line-clamp-2 mb-4">
                      {layout.desc}
                    </p>

                    {/* Specs footer row with icons */}
                    <div className="flex flex-wrap items-center gap-3 text-[11px] text-gray-500 font-semibold mt-auto pt-2 border-t border-gray-100">
                      {layout.beds && (
                        <div className="flex items-center gap-1">
                          <svg
                            className="w-3.5 h-3.5 text-gray-400"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="2"
                              d="M3 10h18M3 14h18m-2-4v8m-14-8v8m1-8h12M5 6h14a2 2 0 012 2v2H3V8a2 2 0 012-2z"
                            />
                          </svg>
                          <span>{layout.beds} PN</span>
                        </div>
                      )}
                      {layout.baths && (
                        <div className="flex items-center gap-1">
                          <svg
                            className="w-3.5 h-3.5 text-gray-400"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="2"
                              d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"
                            />
                          </svg>
                          <span>{layout.baths} WC</span>
                        </div>
                      )}
                      {layout.area && (
                        <div className="flex items-center gap-1">
                          <svg
                            className="w-3.5 h-3.5 text-gray-400"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="2"
                              d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5v-4m0 4h-4m4 0l-5-5"
                            />
                          </svg>
                          <span>{layout.area}</span>
                        </div>
                      )}
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          onConsultClick?.(layout.title);
                        }}
                        className="ml-auto bg-primary hover:bg-third text-white text-[9px] font-extrabold uppercase px-2.5 py-1 rounded transition duration-200 focus:outline-none cursor-pointer"
                      >
                        Nhận tư vấn
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </motion.div>
          </div>

          {/* Pagination Dots */}
          {totalDots > 1 && (
            <div className="flex items-center justify-center gap-2 pt-2">
              {Array.from({ length: totalDots }).map((_, idx) => {
                const isActive = activeIndex === idx;
                return (
                  <button
                    key={idx}
                    onClick={() => handleGoTo(idx)}
                    className={cn(
                      "h-2 rounded-full transition-all duration-300 cursor-pointer focus:outline-none",
                      isActive
                        ? "w-7 bg-primary"
                        : "w-2 bg-black/20 hover:bg-black/40",
                    )}
                    aria-label={`Go to slide ${idx + 1}`}
                  />
                );
              })}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
